import React, { useEffect } from "react";
import axios, { AxiosHeaders } from "axios";
import { useAuth0 } from "@auth0/auth0-react";

type Auth0AxiosBridgeProps = {
  audience?: string;
};

/**
 * Renders nothing; exists to install GLOBAL axios interceptors while mounted inside Auth0Provider.
 * All API access : the generated OpenAPI clients and ad-hoc axios calls alike : goes through the
 * axios default instance, so this one bridge is what authenticates the entire admin. Any client
 * that bypasses axios (raw fetch) is NOT authenticated and will 401 on a deployed instance.
 *
 * Request side: attaches a Bearer token from the Auth0 SDK cache (silent renewal when expired),
 * unless the caller already set Authorization explicitly. Token failures let the request proceed
 * unauthenticated : the 401 path below owns recovery, and public endpoints keep working.
 *
 * Response side: on a 401 it forces ONE fresh token (cacheMode "off", since the cached token just
 * proved stale) and replays the original request; `_retry` marks the config so a second 401 can't
 * loop. If even the forced refresh fails, the session is truly dead → logout to Auth0.
 */
export function Auth0AxiosBridge({ audience }: Auth0AxiosBridgeProps): React.ReactElement | null {
  const { getAccessTokenSilently, logout } = useAuth0();

  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use(async (config) => {
      try {
        const token = await getAccessTokenSilently(
          audience ? { authorizationParams: { audience } } : undefined
        );
        const headers = AxiosHeaders.from(config.headers ?? {});
        if (!headers.has("Authorization")) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        config.headers = headers;
      } catch {
        // ignore token errors, allow request
      }
      return config;
    });

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error?.config as any;
        if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const token = await getAccessTokenSilently(
              audience ? { authorizationParams: { audience }, cacheMode: "off" as any } : { cacheMode: "off" as any }
            );
            const headers = AxiosHeaders.from(originalRequest.headers ?? {});
            headers.set("Authorization", `Bearer ${token}`);
            originalRequest.headers = headers;
            return axios(originalRequest);
          } catch (refreshError) {
            logout({
              logoutParams: { returnTo: window.location.origin }
            });
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, [getAccessTokenSilently, audience, logout]);

  return null;
}
